import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';

import { TrackerService } from '@services/tracker.service';
import makeDebug from '../../../../makeDebug';
import { AuthService } from '../../services/auth.service';
import { ISyncContext } from '../contracts/sync/sync-context';
import { ISyncElement, SyncElementResult } from '../contracts/sync/sync-element';
import { Authenticator as Authentication } from '../contracts/sync/sync-endpoints';

const debug = makeDebug('sync:authenticator');

@Injectable()
export class Authenticator implements ISyncElement {
  public get name(): string {
    return Authentication;
  }

  constructor(
    private _authenticationService: AuthService,
    private _router: Router,
    private _trackerService: TrackerService,
    private _ngZone: NgZone
  ) {}

  public async execute(context: ISyncContext): Promise<SyncElementResult> {
    debug('entered execute function', context);
    const authenticated = await this.authenticate(context.params.isOnline);
    // In my opinion we need to login if the authenticate didnt work
    // otherwise auth service holds undefined values but we start the sync pipeline which causes errors
    // In offline case the user is set from storage and authenticate resolves true
    // If offline and not user in storage we should not open the app
    if (!authenticated) {
      this._trackerService.debugSyncPipelineElementNotExecuted(this.name, 'notAuthenticated');
      debug('User could not be authenticated.');
      await this._ngZone.run(() => this._router.navigate(['/login']));
      context.params.noAuth = true;
    }
    return { contextId: context.id, authenticated: authenticated, jobDone: true };
  }

  private async authenticate(isOnline: boolean): Promise<boolean> {
    await this._authenticationService.configure();
    let isAuthenticated = await this._authenticationService.authenticate(isOnline);
    if (!isAuthenticated && isOnline && (await this._authenticationService.refreshTokenMobile())) {
      // call configure again on mobile to create new defered with access token
      // which is waited for in authenticate();
      await this._authenticationService.configure();
      debug('accessToken refreshed');
      isAuthenticated = await this._authenticationService.authenticate(isOnline);
      debug('authenticate with new accessToken', isAuthenticated);
    }
    this._authenticationService.setAuthenticated(isAuthenticated, isOnline);
    return isAuthenticated;
  }
}
